import type { CustomerType, Prequalification, ScenarioInput } from '@exigo/shared';
import { formatCount, formatPercent } from '@exigo/shared';
import { Card } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { NumberInput } from '../../components/ui/TextInput';
import { Checkbox } from '../../components/ui/Checkbox';
import styles from './PrequalCard.module.css';
import calcStyles from './CalculatorPage.module.css';

interface PrequalCardProps {
  input: ScenarioInput;
  setInput: (updater: (prev: ScenarioInput) => ScenarioInput) => void;
}

const CUSTOMER_TYPE_OPTIONS = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'strategic500', label: 'Strategic 500' },
  { value: 'edu', label: 'Education' },
  { value: 'nonprofit', label: 'Non-profit' },
];

/**
 * Pre-qualification — customer estate figures and the conditions the tool
 * cannot verify. The percentages shown below each field are a live convenience
 * readout computed from what has been typed, exactly like the workload table's
 * running total; they are NOT the gate. The authoritative pass/fail for every
 * threshold comes back from `/evaluate`, computed once server-side against the
 * same rule for every user — see packages/engine/src/checks/registry.ts.
 */
export function PrequalCard({ input, setInput }: PrequalCardProps): React.JSX.Element {
  const pq = input.prequalification;

  function setField<K extends keyof Prequalification>(key: K, value: Prequalification[K]): void {
    setInput((p) => ({ ...p, prequalification: { ...p.prequalification, [key]: value } }));
  }

  const intuneRatio = pq.m365Seats > 0 ? (pq.intuneSeats / pq.m365Seats) * 100 : null;
  const intuneUsage = pq.intuneSeats > 0 ? (pq.intuneMonthlyActive / pq.intuneSeats) * 100 : null;
  const copilotWhitespace = Math.max(0, pq.copilotPaidUnits - pq.copilotMonthlyActive);
  const mpuWhitespace = Math.max(0, pq.e5PaidUnits - pq.monthlyProtectedUsersDeployed);
  const mpuUsage = pq.e5PaidUnits > 0 ? (pq.monthlyProtectedUsersDeployed / pq.e5PaidUnits) * 100 : null;
  const confirmedCount = [
    pq.cspTermsOk,
    pq.netPaidSeatAdds,
    pq.timingOk,
    pq.withinClaimCaps,
    pq.onPropensityList,
    pq.eaE5Renewal,
    pq.mxdrVerified,
    pq.misaMember,
  ].filter(Boolean).length;

  return (
    <Card className={calcStyles.section}>
      <p className={calcStyles.sectionTitle}>Customer pre-qualification</p>
      <p className={styles.lede}>
        AI Business Solutions and Security do not size on consumption. Eligibility turns on the
        customer&rsquo;s licence estate, their actual usage telemetry, how the transaction is
        papered, when you nominate, and what has already been claimed against the tenant. Anything
        left at zero or unticked blocks the engagements that depend on it, so an export cannot
        overstate what is claimable.
      </p>

      <div className={styles.columns}>
        <div>
          <p className={styles.columnTitle}>Customer and estate</p>
          <div className={styles.fieldStack}>
            <div className={calcStyles.field}>
              <span className={calcStyles.fieldLabel}>Customer type</span>
              <Select
                value={pq.customerType}
                onChange={(v) => { setField('customerType', v as CustomerType); }}
                options={CUSTOMER_TYPE_OPTIONS}
                ariaLabel="Customer type"
              />
              <span className={calcStyles.fieldHint}>
                Strategic 500 is excluded from every engagement in both areas. EDU and non-profit are
                excluded outright from the AI-Ready Productivity engagements, and from the XXS and XS
                bands elsewhere.
              </span>
            </div>
            <NumField
              label="Purchased Office 365 + Microsoft 365 seats"
              value={pq.m365Seats}
              onChange={(v) => { setField('m365Seats', v); }}
            />
            <NumField
              label="Intune seats purchased"
              value={pq.intuneSeats}
              onChange={(v) => { setField('intuneSeats', v); }}
              hint="ME3 Envisioning needs Intune at 50% or less of total seats."
            />
            <NumField
              label="Intune monthly active users"
              value={pq.intuneMonthlyActive}
              onChange={(v) => { setField('intuneMonthlyActive', v); }}
              hint="Cloud Endpoints Envisioning needs Intune usage at 40% or more of Intune seats — the opposite test to ME3."
            />
            <NumField
              label="Entra ID P1 monthly active usage, % of seats"
              value={pq.entraP1UsagePct}
              onChange={(v) => { setField('entraP1UsagePct', v); }}
              max={100}
              hint="The ME3 route into Security Envisioning needs 80%. The ME5 route has no usage test."
            />
          </div>
        </div>

        <div>
          <p className={styles.columnTitle}>Copilot and E5 whitespace</p>
          <div className={styles.fieldStack}>
            <NumField
              label="Copilot seats purchased (paid available units)"
              value={pq.copilotPaidUnits}
              onChange={(v) => { setField('copilotPaidUnits', v); }}
            />
            <NumField
              label="Copilot monthly active users"
              value={pq.copilotMonthlyActive}
              onChange={(v) => { setField('copilotMonthlyActive', v); }}
              hint="The Copilot Deployment Accelerator needs unused Copilot capacity at least equal to the band's seat threshold. A customer with strong adoption is ineligible."
            />
            <NumField
              label="E5 security paid available units"
              value={pq.e5PaidUnits}
              onChange={(v) => { setField('e5PaidUnits', v); }}
            />
            <NumField
              label="Monthly protected users already deployed"
              value={pq.monthlyProtectedUsersDeployed}
              onChange={(v) => { setField('monthlyProtectedUsersDeployed', v); }}
              hint="Whitespace and usage percentage for both ME5 Usage Accelerators are derived from these two figures. Data Security needs usage below 50%, XDR below 40%."
            />
            <NumField
              label="Security workshops claimed against this tenant in FY26"
              value={pq.fy26SecurityWorkshops}
              onChange={(v) => { setField('fy26SecurityWorkshops', v); }}
              hint="Three or more, by any partner, closes the tenant to FY27 Security pre-sales entirely."
            />
          </div>
        </div>

        <div>
          <p className={styles.columnTitle}>Conditions the tool cannot verify</p>
          <p className={styles.columnNote}>
            Each of these blocks its engagements until confirmed. Confirm only what you have actually
            established — these statements carry into the export.
          </p>
          <div className={styles.checkStack}>
            <Checkbox checked={pq.cspTermsOk} onChange={(v) => { setField('cspTermsOk', v); }}>
              CSP annual term with 6+ months remaining
            </Checkbox>
            <Checkbox checked={pq.netPaidSeatAdds} onChange={(v) => { setField('netPaidSeatAdds', v); }}>
              Net paid seat adds, same tenant and month
            </Checkbox>
            <Checkbox checked={pq.timingOk} onChange={(v) => { setField('timingOk', v); }}>
              Nominated pre-sale or within 2 months of provisioning
            </Checkbox>
            <Checkbox checked={pq.withinClaimCaps} onChange={(v) => { setField('withinClaimCaps', v); }}>
              Tenant within approved and concurrent claim caps
            </Checkbox>
            <Checkbox checked={pq.onPropensityList} onChange={(v) => { setField('onPropensityList', v); }}>
              On the GTM pre-published propensity list
            </Checkbox>
            <Checkbox checked={pq.eaE5Renewal} onChange={(v) => { setField('eaE5Renewal', v); }}>
              Non-unified EA, commercial E5, FY27/FY28 renewal
            </Checkbox>
            <Checkbox checked={pq.mxdrVerified} onChange={(v) => { setField('mxdrVerified', v); }}>
              MXDR verified practice
            </Checkbox>
            <Checkbox checked={pq.misaMember} onChange={(v) => { setField('misaMember', v); }}>
              Microsoft Intelligent Security Association member
            </Checkbox>
          </div>
        </div>
      </div>

      <div className={styles.derived}>
        <DerivedCell label="Intune penetration" value={pctLabel(intuneRatio)} good={intuneRatio !== null && intuneRatio <= 50} />
        <DerivedCell label="Intune active usage" value={pctLabel(intuneUsage)} good={intuneUsage !== null && intuneUsage >= 40} />
        {/* The ME3 route into Security Envisioning needs 80%; the ME5 route has
            no usage test at all, so this is reported rather than judged. */}
        <DerivedCell
          label="Entra P1 usage"
          value={pq.entraP1UsagePct > 0 ? pctLabel(pq.entraP1UsagePct) : '—'}
        />
        <DerivedCell label="Copilot whitespace" value={`${formatCount(copilotWhitespace)} seats`} />
        <DerivedCell label="MPU whitespace" value={`${formatCount(mpuWhitespace)} users`} />
        <DerivedCell label="E5 protection coverage" value={pctLabel(mpuUsage)} />
        {/* Three or more, by any partner, closes the tenant to FY27 Security
            pre-sales entirely — so it is flagged rather than shown neutrally. */}
        <DerivedCell
          label="FY26 security workshops"
          value={formatCount(pq.fy26SecurityWorkshops)}
          good={pq.fy26SecurityWorkshops < 3}
        />
        <DerivedCell label="Conditions confirmed" value={`${String(confirmedCount)} of 8`} good={confirmedCount === 8} />
      </div>
    </Card>
  );
}

function NumField({
  label,
  value,
  onChange,
  max,
  hint,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
  /** The guide's own note on what this figure is tested against. */
  hint?: string;
}): React.JSX.Element {
  return (
    <div className={calcStyles.field}>
      <label className={calcStyles.fieldLabel}>{label}</label>
      <NumberInput value={value} onValueChange={onChange} min={0} ariaLabel={label} {...(max === undefined ? {} : { max })} />
      {hint && <span className={calcStyles.fieldHint}>{hint}</span>}
    </div>
  );
}

/**
 * One decimal, or two below 0.1%.
 *
 * Uses the shared formatter rather than a local `toFixed(1)`: these percentages
 * are read against thresholds like the 4% sub-floor and the 40%/50% usage tests,
 * and a genuinely tiny share flattening to `0.0%` reads as zero when it is not.
 */
function pctLabel(pct: number | null): string {
  return formatPercent(pct);
}

function DerivedCell({ label, value, good }: { label: string; value: string; good?: boolean }): React.JSX.Element {
  const cls =
    good === undefined ? styles.derivedCell : good ? `${styles.derivedCell} ${styles.derivedGood}` : `${styles.derivedCell} ${styles.derivedBad}`;
  return (
    <div className={cls}>
      {label}: <span className={styles.derivedValue}>{value}</span>
    </div>
  );
}
